import React from 'react';
import { useFormController } from '@app/hooks/useFormController';

import './FormSelectDropdown.scss';

import TnkDropdown from '../TnkDropdown';

export interface DropdownOption {
  label: string;
  value: any;
}

type FormSelectDropdownProps = {
  name: string;
  placeholder?: string;
  options: DropdownOption[];
  disabled?: boolean;
  className?: string;
  label?: string;
  inputOnly?: boolean;
  required?: boolean;
  value?: any;
  inputStyle?: React.CSSProperties;
  defaultOptional?: string;
  defaultValue?: string | number | null;
  onChange?: (value: any) => void;
  optionDisabled?: (option: DropdownOption) => boolean;
};

const DEFAULT_SENTINEL = '__ALL__';

const FormSelectDropdown: React.FC<FormSelectDropdownProps> = ({
  name,
  placeholder,
  options,
  disabled = false,
  className = '',
  label,
  inputOnly = false,
  required = false,
  value,
  inputStyle,
  defaultOptional,
  defaultValue,
  onChange,
  optionDisabled,
}) => {
  const { field, fieldState } = useFormController({ name });

  const resolvedOptions = defaultOptional
    ? [{ label: defaultOptional, value: defaultValue ?? DEFAULT_SENTINEL }, ...options]
    : options;

  // Map null → sentinel for display, sentinel → null on change
  const displayValue = (() => {
    const raw = value === undefined ? field.value : value;
    if (defaultOptional && (raw === null || raw === undefined)) return DEFAULT_SENTINEL;
    return raw;
  })();

  const handleChange = (val: any) => {
    const mapped = defaultOptional && val === DEFAULT_SENTINEL ? null : val;
    field.onChange(mapped);
    if (typeof onChange === 'function') {
      onChange(mapped);
    }
  };

  const valueTemplate = (option: DropdownOption | null) => {
    if (!option) {
      const matched = resolvedOptions.find((opt) => opt.value === displayValue);
      return matched ? matched.label : '';
    }
    return option.label;
  };

  return (
    <div className={`flex flex-column field-name${name} ${className}`}>
      {label && !inputOnly && (
        <label htmlFor={name} className="block body-sx fw-400 mb-1">
          {label}
          {required && <span style={{ color: 'var(--color-danger)', marginLeft: '4px' }}>*</span>}
        </label>
      )}
      <TnkDropdown
        name={field.name}
        ref={field.ref}
        onBlur={field.onBlur}
        inputId={name}
        options={resolvedOptions}
        placeholder={placeholder}
        disabled={disabled}
        className={`select-dropdown-custom ${fieldState.invalid ? 'p-invalid' : ''}`}
        value={displayValue}
        onChange={(e) => handleChange(e.value)}
        valueTemplate={valueTemplate}
        style={inputStyle}
        optionDisabled={optionDisabled}
      />
      {fieldState.invalid && <small className="p-error body-sx mt-1">{fieldState?.error?.message}</small>}
    </div>
  );
};

export { FormSelectDropdown };
